import type { Feature } from 'ol';
import type { Geometry } from 'ol/geom';
import { Draw, Modify } from 'ol/interaction.js';

import { DrawToolType } from '@nora-monorepo/nora-api-client';

import { CreateFeatureBaseDrawTool } from '#nora-ui/map/drawtools/create-feature-base-draw-tool';
import { getHandleModifyPixelTolerance } from '#nora-ui/map/drawtools/draw-tool-utils';
import { rulerModifyStyle } from '#nora-ui/map/drawtools/styles/ruler-style';
import { getFeatureStyle } from '#nora-ui/map/drawtools/styles/styles';
import type { DrawToolOptions } from '#nora-ui/map/drawtools/types';
import type { DrawToolManager } from '#nora-ui/map/map/DrawToolManager';

export class RulerTool extends CreateFeatureBaseDrawTool {
  drawToolType: DrawToolType = DrawToolType.ruler;
  options: DrawToolOptions = {
    drawToolType: this.drawToolType,
  };

  private readonly drawInteraction: Draw;
  private readonly modifyInteraction: Modify;

  constructor(drawToolManager: DrawToolManager) {
    super(drawToolManager);
    this.drawToolName = 'Ruler';

    this.drawInteraction = new Draw({
      source: this.editingVectorSource,
      type: 'LineString',
      style: getFeatureStyle,
    });

    this.modifyInteraction = new Modify({
      source: this.editingVectorSource,
      style: rulerModifyStyle,
      pixelTolerance: getHandleModifyPixelTolerance(),
    });

    this.modifyInteraction.setActive(true);

    this.drawInteraction.on('drawstart', (event) => {
      event.feature.setProperties(this.getDefaultProperties());
      event.feature.setStyle(getFeatureStyle);
      this.markDirty();
    });

    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      feature.setProperties(this.getDefaultProperties());
      feature.setStyle(getFeatureStyle);
      this.modifyInteraction.setActive(true);
      this.feature = feature;
    });
  }

  override activate(): void {
    super.activate();
    const map = this.drawToolManager.map.getOlMapOrThrow();
    map.addInteraction(this.drawInteraction);
    map.addInteraction(this.modifyInteraction);
  }

  override deactivate(): void {
    super.deactivate();
    const map = this.drawToolManager.map.getOlMapOrThrow();
    map.removeInteraction(this.drawInteraction);
    map.removeInteraction(this.modifyInteraction);
  }

  override showStyles(): boolean {
    return false;
  }

  override async save(_feature?: Feature<Geometry> | null): Promise<void> {
    this.drawInteraction.finishDrawing();
    await super.save(_feature);
  }
}
